import React from "react";

// One-page printer specification brief for the Sage Imprint card.
// 8.5" × 11" portrait — designed to be emailed/printed and handed to any print shop.
// Includes paper, ink, foil, deboss, bleed and file format specs.
export default function PrinterBrief({ brand = "Let Go Wellness", phone, email }) {
  const Row = ({ label, value, mono }) => (
    <div
      className="grid mb-3"
      style={{ gridTemplateColumns: "1.7in 1fr", borderBottom: "1px solid #E2DCD0", paddingBottom: "0.08in" }}
    >
      <p
        className="uppercase"
        style={{ fontSize: "0.08in", letterSpacing: "0.22em", color: "#9F5C45", paddingTop: "0.02in" }}
      >
        {label}
      </p>
      <p
        style={{
          fontSize: "0.11in",
          color: "#1C1917",
          lineHeight: 1.45,
          fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
        }}
      >
        {value}
      </p>
    </div>
  );

  const Swatch = ({ color, label, hex, cmyk, pantone }) => (
    <div className="flex items-start gap-3 mb-2">
      <span
        className="border border-[#E2DCD0] flex-shrink-0"
        style={{ width: "0.55in", height: "0.55in", background: color }}
      />
      <div>
        <p
          className="uppercase"
          style={{ fontSize: "0.07in", letterSpacing: "0.22em", color: "#9F5C45" }}
        >
          {label}
        </p>
        <p style={{ fontSize: "0.1in", color: "#1C1917", marginTop: "0.02in" }}>{hex}</p>
        <p style={{ fontSize: "0.08in", color: "#57534E" }}>{cmyk}</p>
        {pantone && (
          <p style={{ fontSize: "0.08in", color: "#57534E" }}>Pantone {pantone}</p>
        )}
      </div>
    </div>
  );

  return (
    <div
      className="relative bg-[#FDFBF7] text-[#1C1917] font-sans"
      style={{ width: "8.5in", height: "11in", padding: "0.7in 0.85in" }}
    >
      {/* Header */}
      <header className="flex items-end justify-between border-b border-[#1C1917] pb-5 mb-7">
        <div>
          <p
            className="uppercase"
            style={{ fontSize: "0.085in", letterSpacing: "0.32em", color: "#9F5C45" }}
          >
            Printer specification
          </p>
          <h1
            className="font-serif font-light text-[#1C1917] mt-2 tracking-tight"
            style={{ fontSize: "0.5in", lineHeight: 1 }}
          >
            {brand} · Business Card
          </h1>
          <p
            className="font-serif italic mt-1"
            style={{ fontSize: "0.16in", color: "#3A5A40" }}
          >
            Sage Imprint · blind deboss + gold foil stamp
          </p>
        </div>
        <p
          className="font-serif text-right"
          style={{ fontSize: "0.13in", color: "#57534E" }}
        >
          v1.0
        </p>
      </header>

      {/* Body */}
      <section style={{ marginBottom: "0.25in" }}>
        <p
          className="uppercase"
          style={{ fontSize: "0.08in", letterSpacing: "0.28em", color: "#3A5A40", marginBottom: "0.12in" }}
        >
          01 · Dimensions & Stock
        </p>
        <Row label="Final Size" value="3.5″ × 2″ (89 mm × 51 mm), landscape" />
        <Row label="Stock" value="32 pt (~600 gsm) duplexed sage cotton OR uncoated soft-touch" />
        <Row label="Finish" value="Uncoated matte (recommended) — avoid gloss; foil shows best on matte" />
        <Row label="Corners" value="Standard square cut. Optional 1/8″ rounded corners if desired." />
        <Row label="Quantity" value="Recommended first run: 500 per person (1,000 total)" />
      </section>

      <section style={{ marginBottom: "0.25in" }}>
        <p
          className="uppercase"
          style={{ fontSize: "0.08in", letterSpacing: "0.28em", color: "#3A5A40", marginBottom: "0.12in" }}
        >
          02 · Color
        </p>
        <div className="grid grid-cols-3 gap-4">
          <Swatch color="#3A5A40" label="Sage (paper)" hex="HEX #3A5A40" cmyk="CMYK 60 / 20 / 65 / 50" pantone="5535 C" />
          <Swatch color="#FDFBF7" label="Cream (ink, back side)" hex="HEX #FDFBF7" cmyk="CMYK 1 / 1 / 3 / 0" pantone="11-0602 TPX" />
          <Swatch
            color="linear-gradient(135deg,#8E6F2D,#F2D77A,#9C7A2E)"
            label="Gold foil"
            hex="Foil — see spec"
            cmyk="Kurz LUXOR 220 or equivalent"
            pantone="871 C metallic"
          />
        </div>
      </section>

      <section style={{ marginBottom: "0.25in" }}>
        <p
          className="uppercase"
          style={{ fontSize: "0.08in", letterSpacing: "0.28em", color: "#3A5A40", marginBottom: "0.12in" }}
        >
          03 · Front · Decoration
        </p>
        <Row
          label="Wordmark"
          value='"LET GO / WELLNESS" set in Cormorant Garamond Light, uppercase, 2 stacked lines.'
        />
        <Row
          label="Decoration"
          value="BLIND DEBOSS (no ink, no foil) on the wordmark. Depth: ~0.020″ recommended."
        />
        <Row
          label="Centered logo"
          value="Winged figure mark in GOLD FOIL STAMP. Size: ~1″ tall, centered between the two text lines."
        />
        <Row
          label="Vertical edge text"
          value="[Person name] · [Credentials] in light blind deboss on right edge (90° rotated)."
        />
        <Row
          label="Small details"
          value='"LET GO WELLNESS" in 0.07″ deboss top-left; 0.07″ deboss dot bottom-left.'
        />
      </section>

      <section style={{ marginBottom: "0.25in" }}>
        <p
          className="uppercase"
          style={{ fontSize: "0.08in", letterSpacing: "0.28em", color: "#3A5A40", marginBottom: "0.12in" }}
        >
          04 · Back · Content
        </p>
        <Row
          label="Print method"
          value="Single-sided digital or offset CMYK in cream/white on sage stock. No foil on back."
        />
        <Row
          label="Layout"
          value="Person name + credentials top, contact (phone · email · address) bottom, domain on last line."
        />
      </section>

      <section style={{ marginBottom: "0.25in" }}>
        <p
          className="uppercase"
          style={{ fontSize: "0.08in", letterSpacing: "0.28em", color: "#3A5A40", marginBottom: "0.12in" }}
        >
          05 · File deliverables
        </p>
        <Row
          label="Working file"
          value="PNG @ 300 dpi · 1200 × 700 px (3.5″ × 2″ + 0.125″ bleed)"
          mono
        />
        <Row label="Bleed" value="0.125″ on all sides (full-bleed sage)" />
        <Row label="Safe zone" value="0.1875″ from trim — keep all type & logo inside" />
        <Row label="Color space" value="CMYK preferred; sRGB accepted if printer converts" />
      </section>

      {/* Footer */}
      <footer
        className="absolute bottom-0 left-0 right-0 flex items-center justify-between"
        style={{ padding: "0.4in 0.85in 0.6in", borderTop: "1px solid #E2DCD0" }}
      >
        <div>
          <p
            className="uppercase"
            style={{ fontSize: "0.075in", letterSpacing: "0.28em", color: "#9F5C45" }}
          >
            Questions
          </p>
          <p style={{ fontSize: "0.11in", color: "#1C1917", marginTop: "0.02in" }}>{phone}</p>
          <p style={{ fontSize: "0.1in", color: "#57534E" }}>{email}</p>
        </div>
        <p
          className="font-serif italic text-right"
          style={{ fontSize: "0.12in", color: "#3A5A40", maxWidth: "3.5in" }}
        >
          Thank you for taking care with this card. Every detail matters.
        </p>
      </footer>
    </div>
  );
}
